import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import palette from '../../lib/styles/palette';
import Table from '../common/Table';
import { Switch } from '@material-ui/core';
import { FiPlus } from 'react-icons/fi';

const Wrapper = styled.div`
  width: 100%;
  margin-top: 24px;
  padding: 0 20px 40px 20px;
`;

const SectionCard = styled.div`
  background: #fff;
  border: 1px solid #e0e4ea;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
`;

const HeaderRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 18px 24px;
  background: #f8f9fb;
  border-bottom: 1px solid #e0e4ea;
`;

const Title = styled.div`
  font-size: 1.15rem;
  font-weight: 700;
  color: #2c3e50;
`;

const Controls = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const Body = styled.div`
  padding: 24px;
`;

const SearchInput = styled.input`
  width: 280px;
  padding: 10px 12px;
  border: 1px solid ${palette.inputBorder};
  border-radius: 6px;
  font-size: 0.95rem;
  color: #555;
  transition: border-color 0.15s ease;

  &:focus {
    outline: none;
    border-color: ${palette.inputBorderFocus};
  }

  &::placeholder {
    color: #868e96;
  }
`;

const Button = styled.button`
  padding: 10px 16px;
  border-radius: 6px;
  border: 1px solid ${palette.webCyan[5]};
  background: #fff;
  color: ${palette.webCyan[5]};
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 600;
  transition: all 0.15s ease;
  &:hover {
    background: ${palette.webCyan[5]};
    color: #fff;
  }
`;

const Message = styled.div`
  width: 100%;
  padding: 12px 16px;
  background: #fff5f5;
  border: 1px solid #f3c9c9;
  border-radius: 6px;
  color: #e74c3c;
  font-size: 0.9rem;
  margin-bottom: 20px;
`;

const SwitchWrap = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
`;

const SwitchLabel = styled.span`
  font-size: 0.9rem;
  color: #868e96;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalBox = styled.div`
  background: #fff;
  border: 1px solid #e0e4ea;
  border-radius: 12px;
  padding: 24px;
  min-width: 400px;
  max-width: 480px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const ModalTitle = styled.h3`
  margin: 0 0 20px 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #2c3e50;
`;

const FormRow = styled.div`
  margin-bottom: 16px;
  label {
    display: block;
    margin-bottom: 6px;
    font-size: 0.9rem;
    font-weight: 600;
    color: #555;
  }
  input {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid ${palette.inputBorder};
    border-radius: 6px;
    font-size: 0.95rem;
    color: #555;
    box-sizing: border-box;
    transition: border-color 0.15s ease;
  }
  input:focus {
    outline: none;
    border-color: ${palette.inputBorderFocus};
  }
  input::placeholder {
    color: #868e96;
  }
`;

const ModalButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #e0e4ea;
`;

const ModalCancelBtn = styled(Button)`
  padding: 10px 18px;
`;

const ModalSubmitBtn = styled.button`
  padding: 10px 18px;
  border-radius: 6px;
  border: none;
  background: ${palette.webCyan[5]};
  color: #fff;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s ease;
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const AddCenterButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 6px;
  border: none;
  background: ${palette.webCyan[5]};
  color: #fff;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s ease;
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const AdminCenterSetting = ({
  centers,
  loading,
  error,
  keyword,
  onChangeKeyword,
  onEditManagerFlag,
  savingIds,
  onCreateCenter,
}) => {
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    centerName: '',
    account: '',
    password: '',
    phone: '',
  });

  const filtered = useMemo(() => {
    if (!Array.isArray(centers)) return [];
    const q = (keyword || '').trim().toLowerCase();
    if (!q) return centers;
    return centers.filter((c) =>
      String(c.name || '').toLowerCase().includes(q),
    );
  }, [centers, keyword]);

  const tableData = useMemo(() => {
    return filtered.map((c) => ({
      centerID: c.centerID,
      name: c.name,
      ownerID: c.ownerID,
      dashboardToggle: (
        <SwitchWrap>
          <Switch
            color="primary"
            checked={Number(c.managerFlag || 0) === 1}
            disabled={savingIds && savingIds.has && savingIds.has(Number(c.centerID))}
            onChange={() =>
              onEditManagerFlag &&
              onEditManagerFlag(c.centerID, Number(c.managerFlag || 0))
            }
          />
          {savingIds && savingIds.has && savingIds.has(Number(c.centerID)) ? (
            <SwitchLabel>저장중...</SwitchLabel>
          ) : null}
        </SwitchWrap>
      ),
    }));
  }, [filtered, onEditManagerFlag, savingIds]);

  const handleCreateChange = (e) => {
    const { name, value } = e.target;
    setCreateForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreateSubmit = async () => {
    if (!onCreateCenter) return;
    const { centerName, account, password, phone } = createForm;
    if (!centerName.trim()) {
      alert('센터명을 입력해주세요.');
      return;
    }
    if (!account.trim()) {
      alert('센터 소유자 아이디(이메일)를 입력해주세요.');
      return;
    }
    if (!password.trim() || password.trim().length < 6) {
      alert('비밀번호를 6자리 이상 입력해주세요.');
      return;
    }
    if (!phone.trim()) {
      alert('전화번호를 입력해주세요.');
      return;
    }

    const ok = window.confirm('새 센터를 생성하시겠습니까?');
    if (!ok) return;

    try {
      setCreating(true);
      const result = await onCreateCenter({
        centerName: centerName.trim(),
        account: account.trim(),
        password: password.trim(),
        phone: phone.trim(),
      });
      if (result !== false) {
        setCreateForm({
          centerName: '',
          account: '',
          password: '',
          phone: '',
        });
        setCreateOpen(false);
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <Wrapper>
      {createOpen && (
        <ModalOverlay onClick={() => setCreateOpen(false)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalTitle>새 센터 생성</ModalTitle>
            <FormRow>
              <label>센터명</label>
              <input
                name="centerName"
                placeholder="센터명 입력"
                value={createForm.centerName}
                onChange={handleCreateChange}
              />
            </FormRow>
            <FormRow>
              <label>센터 소유자 아이디(이메일)</label>
              <input
                name="account"
                placeholder="이메일 입력"
                value={createForm.account}
                onChange={handleCreateChange}
              />
            </FormRow>
            <FormRow>
              <label>비밀번호 (6자리 이상)</label>
              <input
                name="password"
                type="password"
                placeholder="비밀번호 입력"
                value={createForm.password}
                onChange={handleCreateChange}
              />
            </FormRow>
            <FormRow>
              <label>전화번호</label>
              <input
                name="phone"
                placeholder="전화번호 입력"
                value={createForm.phone}
                onChange={handleCreateChange}
              />
            </FormRow>
            <ModalButtons>
              <ModalCancelBtn onClick={() => setCreateOpen(false)}>
                취소
              </ModalCancelBtn>
              <ModalSubmitBtn onClick={handleCreateSubmit} disabled={creating}>
                {creating ? '생성 중...' : '센터 생성'}
              </ModalSubmitBtn>
            </ModalButtons>
          </ModalBox>
        </ModalOverlay>
      )}

      <SectionCard>
        <HeaderRow>
          <Title>센터 설정</Title>
          <Controls>
            <SearchInput
              value={keyword}
              onChange={onChangeKeyword}
              placeholder="센터명 검색"
            />
            {onCreateCenter && (
              <AddCenterButton
                onClick={() => setCreateOpen(true)}
                disabled={loading}
              >
                <FiPlus size={18} />
                센터 생성
              </AddCenterButton>
            )}
          </Controls>
        </HeaderRow>

        <Body>
          {error ? <Message>{error}</Message> : null}

          <Table
            th={['센터ID', '센터명', 'OwnerID', '대시보드 사용']}
            renderingCol={['centerID', 'name', 'ownerID', 'dashboardToggle']}
            data={tableData}
          />
        </Body>
      </SectionCard>
    </Wrapper>
  );
};

export default React.memo(AdminCenterSetting);

